import { SortingHeaderButton } from '@/components/ui/DataTable/SortingHeaderButton'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { CellContext, Column, ColumnDef } from '@tanstack/react-table'
import { RenderCell } from './RenderCell'
import { RowActionsMenu } from './RowActions'
import { getSortingFn } from './sortingFunctions'
import { TableUiSchema } from './types-v4'

/**
 * Renders the column header with optional sorting and tooltip
 */
const RenderColumnHeader = <T,>({
  column,
  title,
  description,
  sortingEnabled,
}: {
  column: Column<T>
  title: string
  description?: string
  sortingEnabled?: boolean
}) => {
  if (sortingEnabled) {
    return (
      <SortingHeaderButton<T> column={column} description={description}>
        {title}
      </SortingHeaderButton>
    )
  }

  return (
    <div className="flex items-center gap-x-1 font-bold">
      {description ? (
        <TooltipButton tooltip={description} contentClassName="font-light text-sm p-5">
          <span>{title}</span>
        </TooltipButton>
      ) : (
        <span>{title}</span>
      )}
    </div>
  )
}

/**
 * Creates column definitions for the table based on the schema
 */
export function getColumns<T>(tableSchema: TableUiSchema): ColumnDef<T>[] {
  const accessorColumns = tableSchema.columns
    .filter((col) => col.columnConfig.visibility.visible)
    .map((col) => ({
      id: col.id,
      accessorKey: col.id,
      header: ({ column }: { column: Column<T> }) => (
        <RenderColumnHeader
          column={column}
          title={col.title}
          description={col.description}
          sortingEnabled={col.columnConfig.sorting?.enabled}
        />
      ),
      cell: (info: CellContext<T, unknown>) => (
        <RenderCell
          display={col.columnConfig.display}
          editing={col.columnConfig.editing}
          metadata={col.columnConfig.metadata}
          value={info.getValue()}
        />
      ),
      sortingFn: getSortingFn<T>(col.id, col.columnConfig.editing?.type, col.columnConfig.editing?.format),
      size: col.columnConfig.display?.width,
    }))

  const rowActionColumn = tableSchema.actions?.row?.length
    ? {
        id: 'rowActions',
        cell: (info: CellContext<T, unknown>) => <RowActionsMenu info={info} tableSchema={tableSchema} />,
        size: 48,
      }
    : undefined

  return rowActionColumn ? [...accessorColumns, rowActionColumn] : accessorColumns
}
